/**
 * DocumentsListPage Component
 *
 * Main page component for viewing and managing documents.
 * Features:
 * - MUI DataGrid with server-side pagination
 * - Comprehensive filtering (search, type, date range)
 * - Sortable columns
 * - Click to open document detail modal
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { kindIcon, kindLabel, kindStyle } from '@components/DocumentKind';
import PatientPicker from '@components/Patient/PatientPicker';
import { EntitlementGate, EntitlementUsage } from '@features/entitlements';
import { GUIDE_SURFACES, GuideLink } from '@features/knowledge-base/links';
import { useGetV2ProviderDocumentsPromptsQuery } from '@features/markdown-editor-ai/store/api';
import type { CustomPromptResponse } from '@features/markdown-editor-ai/types';
import {
  ArtifactDropOverlay,
  formatDuplicateMessage,
  formatUploadLimitError,
  PendingUploadCard,
  UploadFilesButton,
  useArtifactUploadConstraints,
  useChartWideDropZone,
  useOptimisticUploads,
  useOrganizationArtifactEventStream,
  usePatientArtifactEventStream,
  useUploadOrganizationArtifacts,
  useUploadPatientArtifacts,
  validateFiles,
} from '@features/patient-artifacts';
import { CustomTemplateSelector } from '@features/toolbar/actions/CustomTemplateSelector';
import { useOrganizationId, useSubjectLanguageVariants } from '@features/user';
import { usePatientById } from '@hooks/usePatientById';
import { Avatar } from '@jotpsych/ui';
import {
  Description as DocumentIcon,
  Download as DownloadIcon,
  LibraryBooks as LibraryIcon,
  Send as SendIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridRowParams,
  type GridSortModel,
} from '@mui/x-data-grid';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks/useModal';
import { useSnackbar } from 'notistack';

import type { Patient } from '../../../types/types';
import { type SortField, useDocumentsList } from '../hooks/useDocumentsList';
import { useLazyGetDocumentQuery } from '../store/api';
import type { DocumentSummaryResponse } from '../types';
import { formatDocumentDisplayDateMedium } from '../utils/documentDisplayDate';

import DocumentDetailModal from './DocumentDetailModal';
import DocumentFilters from './DocumentFilters';
import { DocumentsEmptyState } from './DocumentsEmptyState';

// Document generation templates
const DOCUMENT_TEMPLATES = [
  {
    name: 'ESA Letter',
    prompt: 'Generate a letter verifying the need for an Emotional Support Animal.',
  },
  {
    name: 'Academic Accommodation',
    prompt: 'Generate a letter requesting academic accommodations.',
  },
  {
    name: 'Workplace Accommodation',
    prompt: 'Generate a letter requesting workplace accommodations.',
  },
  {
    name: 'Treatment Plan',
    prompt: 'Generate a treatment plan outlining goals and interventions.',
  },
];

export const DocumentsListPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { singularCap: subjectLanguageCap } = useSubjectLanguageVariants();
  const { document: documentMetrics } = useEventTracking();
  const { open } = useModal();

  // Document download hook - fetch blob directly via proxy
  // Source-aware: detail endpoint returns a presigned `download_url` that
  // resolves to the right S3 object regardless of `source` (DocRef-backed
  // or legacy DocumentMetadata). The proxy endpoint only knows about
  // legacy rows, so it 404s for DocRef-backed downloads.
  const [fetchDocumentDetail] = useLazyGetDocumentQuery();

  // Track if we've already fired the list loaded event
  const hasTrackedListLoad = useRef(false);

  // Document detail modal state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Document generation state
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [documentTypeInput, setDocumentTypeInput] = useState('');

  // Drop-zone routing (GDN-1032). Files dropped on the page route to the
  // patient artifact endpoint when a patient is selected, or to the
  // org-level endpoint otherwise. The org route lands DocRefs with
  // patient_id=null; both invalidate the unified docs list.
  const organizationId = useOrganizationId();
  const { upload: uploadPatientFiles } = useUploadPatientArtifacts(
    selectedPatient?.patient_id ?? '',
  );
  const { upload: uploadOrgFiles } = useUploadOrganizationArtifacts(organizationId);
  const { constraints: uploadConstraints, summary: uploadConstraintsSummary } =
    useArtifactUploadConstraints();

  // Optimistic-upload state: each successful upload immediately produces a
  // ghost row above the grid. Once the matching DocRef shows up in
  // `documents` (post SSE-driven refetch), `reconcile` drops the ghost.
  // The `useEffect` that calls `reconcile(documents)` is wired further
  // down — it has to run AFTER `documents` is destructured from
  // `useDocumentsList` to avoid a TDZ at component-mount.
  const { pendingUploads, trackUpload, reconcile, handleArtifactEvent } = useOptimisticUploads();

  // Open SSE only while there are pending uploads, then close once
  // they all settle. We subscribe to whichever scope is active right
  // now — patient-scoped if a patient is picked, org-scoped otherwise
  // — so a user who drops a file then waits for it to enrich gets
  // exactly one stream open until the DocRef arrives. `onEvent`
  // advances captions and dismisses on terminal events for both scopes.
  const sseEnabled = pendingUploads.length > 0;
  usePatientArtifactEventStream(selectedPatient?.patient_id ?? null, {
    enabled: sseEnabled && !!selectedPatient?.patient_id,
    onEvent: handleArtifactEvent,
  });
  useOrganizationArtifactEventStream(organizationId, {
    enabled: sseEnabled && !selectedPatient?.patient_id,
    onEvent: handleArtifactEvent,
  });

  const handlePageDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      // Pre-flight against the BE upload limits — see the patient-page
      // drop handler for the same pattern.
      const { accepted, rejected } = validateFiles(files, uploadConstraints);
      for (const r of rejected) {
        enqueueSnackbar(r.reason, { variant: 'warning' });
      }
      if (accepted.length === 0) return;
      const isPatientScoped = !!selectedPatient?.patient_id;
      try {
        const outcome = isPatientScoped
          ? await uploadPatientFiles(accepted)
          : organizationId
            ? await uploadOrgFiles(accepted)
            : null;
        if (!outcome) return;
        trackUpload(outcome);
        for (const dup of outcome.duplicates) {
          enqueueSnackbar(
            formatDuplicateMessage(dup, isPatientScoped ? 'patient' : 'organization'),
            { variant: 'info' },
          );
        }
      } catch (err) {
        console.error('Upload failed:', err);
        enqueueSnackbar(formatUploadLimitError(err), { variant: 'error' });
      }
    },
    [
      selectedPatient,
      organizationId,
      uploadPatientFiles,
      uploadOrgFiles,
      trackUpload,
      enqueueSnackbar,
      uploadConstraints,
    ],
  );

  const { isDragging } = useChartWideDropZone({
    enabled: !!organizationId,
    onDrop: files => {
      void handlePageDrop(files);
    },
  });

  // Fetch custom document templates
  const { data: customTemplatesData } = useGetV2ProviderDocumentsPromptsQuery();
  const hasCustomTemplates = (customTemplatesData?.prompts?.length ?? 0) > 0;

  // Use the documents list hook with fixed document filter
  const {
    documents,
    isLoading,
    isFetching,
    error,
    page,
    pageSize,
    totalRows,
    filters,
    sortBy,
    sortOrder,
    hasActiveFilters,
    setPage,
    setPageSize,
    setPatientId,
    setDocumentType,
    setStartDate,
    setEndDate,
    setSortBy,
    setSortOrder,
    clearFilters,
    // No fixedDocumentType. Pre-GDN-1032 this page pinned `document_type=document`
    // to scope the legacy DocumentMetadata list to markdown exports only. After
    // the unified BE merge, that filter excludes everything coming through the
    // new artifact pipeline (clinical_note, lab_report, discharge_summary, etc.)
    // because the BE translation maps `document_type=document` → DocRef
    // `doc_kind=markdown_doc` only. The unified list now returns DocRefs +
    // legacy rows together; we want to show all of them and let the user pick a
    // doc-kind filter explicitly when they care.
  } = useDocumentsList({ syncWithUrl: false });

  // Reconcile optimistic ghosts against newly-loaded data. Wired here
  // (rather than alongside the rest of the upload state above) because
  // it depends on `documents` from the destructure on line above.
  useEffect(() => {
    reconcile(documents);
  }, [documents, reconcile]);

  // Fetch patient when filter patientId is set (for syncing with form picker)
  const { patient: filterPatient } = usePatientById(filters.patientId || undefined);

  // Sync filter patient to form patient when filter changes
  useEffect(() => {
    if (
      filterPatient &&
      (!selectedPatient || selectedPatient.patient_id !== filterPatient.patient_id)
    ) {
      setSelectedPatient(filterPatient);
    } else if (!filters.patientId && selectedPatient) {
      // Clear form patient when filter is cleared
      setSelectedPatient(null);
    }
  }, [filterPatient, filters.patientId, selectedPatient]);

  // Track document list loaded and page viewed
  useEffect(() => {
    if (!isLoading && !hasTrackedListLoad.current && documents.length >= 0) {
      // Track page viewed
      documentMetrics.pageViewed({
        page_type: 'documents',
        document_count: totalRows,
        has_filters: hasActiveFilters, // Only true if user has other filters (search, date range)
      });
      // Also track list loaded for backwards compatibility
      documentMetrics.listLoaded({
        total_documents: totalRows,
      });
      hasTrackedListLoad.current = true;
    }
  }, [isLoading, documents.length, totalRows, documentMetrics, hasActiveFilters]);

  // Track filter changes
  useEffect(() => {
    if (hasActiveFilters && !isLoading) {
      const filterType = [
        filters.patientId && 'patient',
        filters.documentType && 'document_type',
        (filters.startDate || filters.endDate) && 'date_range',
      ]
        .filter(Boolean)
        .join(',');

      documentMetrics.filtered({
        filter_type: filterType,
        documents_shown: documents.length,
        total_documents: totalRows,
      });
    }
  }, [hasActiveFilters, filters, documents.length, totalRows, isLoading, documentMetrics]);

  // Handle row click to open detail modal
  const handleRowClick = useCallback(
    (params: GridRowParams<DocumentSummaryResponse>) => {
      const doc = params.row;
      // Track row clicked
      documentMetrics.rowClicked({
        document_id: doc.document_id,
        document_type: doc.document_type ?? undefined,
        page_type: 'documents',
        has_patient: !!doc.patient_id,
      });
      // Track document view
      documentMetrics.viewed(doc.document_id, {
        document_type: doc.document_type ?? undefined,
        file_size: doc.size_bytes,
      });
      setSelectedDocumentId(doc.document_id);
      setIsDetailModalOpen(true);
    },
    [documentMetrics],
  );

  // Handle view button click
  const handleViewClick = useCallback(
    (event: React.MouseEvent, document: DocumentSummaryResponse) => {
      event.stopPropagation();
      // Track document view
      documentMetrics.viewed(document.document_id, {
        document_type: document.document_type ?? undefined,
        file_size: document.size_bytes,
      });
      setSelectedDocumentId(document.document_id);
      setIsDetailModalOpen(true);
    },
    [documentMetrics],
  );

  // Handle download button click. Resolves the presigned URL via the
  // detail endpoint (works for both sources) and opens it in a new tab —
  // the BE sets Content-Disposition so the browser will download rather
  // than render inline.
  const handleDownloadClick = useCallback(
    async (event: React.MouseEvent, doc: DocumentSummaryResponse) => {
      event.stopPropagation();
      try {
        const detail = await fetchDocumentDetail({ documentId: doc.document_id }).unwrap();
        if (!detail.download_url) {
          enqueueSnackbar('Download URL not available', { variant: 'error' });
          return;
        }

        documentMetrics.downloaded({
          document_id: doc.document_id,
          document_type: doc.document_type ?? undefined,
          page_type: 'documents',
          file_size: doc.size_bytes,
        });

        window.open(detail.download_url, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.error('Download error:', err);
        enqueueSnackbar('Failed to download document', { variant: 'error' });
      }
    },
    [fetchDocumentDetail, documentMetrics, enqueueSnackbar],
  );

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedDocumentId(null);
  }, []);

  // Handle patient selection (for form panel)
  const handlePatientSelect = useCallback(
    (patient: Patient | undefined) => {
      const patientId = patient?.patient_id || null;
      setSelectedPatient(patient || null);
      // Sync with filter patient picker
      setPatientId(patientId);
    },
    [setPatientId],
  );

  // Handle generate document with prompt
  const handleGenerateDocument = useCallback(
    (prompt?: string) => {
      const finalPrompt = prompt || documentTypeInput.trim();
      if (!finalPrompt || !selectedPatient) return;

      // Track document generation intent
      documentMetrics.created({
        document_type: 'generated',
        source: 'documents_page',
        generation_method: 'ai_generated',
      });
      documentMetrics.generateInitiated({
        method: 'prompt',
        has_patient: true,
        prompt_length: finalPrompt.length,
      });

      // Open the document generator modal with prompt and patient
      open('GENERATE_DOCUMENT', {
        title: 'Generate Documents',
        patientId: selectedPatient.patient_id,
        initialPrompt: finalPrompt,
        maxWidth: 'xl',
        fullWidth: !isMobile,
        fullHeight: !isMobile,
        fullScreen: isMobile,
      });

      // Clear the input after opening modal
      setDocumentTypeInput('');
    },
    [open, isMobile, documentMetrics, documentTypeInput, selectedPatient],
  );

  // Handle template chip click (suggested templates)
  const handleTemplateClick = useCallback(
    (template: { name: string; prompt: string }) => {
      // Track suggested template selection
      documentMetrics.generateInitiated({
        method: 'suggested',
        has_patient: !!selectedPatient,
        template_name: template.name,
      });
      handleGenerateDocument(template.prompt);
    },
    [handleGenerateDocument, documentMetrics, selectedPatient],
  );

  // Handle custom template selection
  const handleCustomTemplateSelect = useCallback(
    (template: CustomPromptResponse) => {
      if (!selectedPatient) return;

      // Track document generation intent
      documentMetrics.created({
        document_type: 'generated',
        source: 'documents_page',
        generation_method: 'template',
      });
      documentMetrics.templateSelected({
        template_id: template.prompt_id,
        template_name: template.name,
        page_type: 'documents',
      });
      documentMetrics.generateInitiated({
        method: 'template',
        has_patient: true,
        template_name: template.name,
      });

      // Open the document generator modal with custom template
      open('GENERATE_DOCUMENT', {
        title: 'Generate Documents',
        patientId: selectedPatient.patient_id,
        customPromptId: template.prompt_id,
        customPromptName: template.name,
        maxWidth: 'xl',
        fullWidth: !isMobile,
        fullHeight: !isMobile,
        fullScreen: isMobile,
      });
    },
    [open, isMobile, documentMetrics, selectedPatient],
  );

  // Handle pagination model change
  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      // DataGrid uses 0-based page index, our API uses 1-based
      const newPage = model.page + 1;
      const newPageSize = model.pageSize;

      // Only update page size if it changed (setPageSize resets page to 1)
      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
      } else if (newPage !== page) {
        // Only update page if page size didn't change
        setPage(newPage);
      }

      // Track pagination change
      const calculatedTotalPages = totalRows > 0 ? Math.ceil(totalRows / newPageSize) : 0;
      documentMetrics.paginationChanged({
        new_page: newPage,
        rows_per_page: newPageSize,
        total_pages: calculatedTotalPages,
        source: 'documents_page',
      });
    },
    [setPage, setPageSize, totalRows, documentMetrics, page, pageSize],
  );

  // Handle sort model change
  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length > 0) {
        const { field, sort } = model[0];
        // Map DataGrid field names to API sort fields
        const fieldMap: Record<string, SortField> = {
          document_title: 'filename',
          created_at: 'created_at',
        };
        const apiField = fieldMap[field] ?? 'created_at';
        setSortBy(apiField);
        setSortOrder(sort ?? 'desc');
        // Track sort change
        documentMetrics.sortChanged({
          sort_field: apiField,
          sort_order: sort ?? 'desc',
          page_type: 'documents',
        });
      } else {
        // Reset to default sort
        setSortBy('created_at');
        setSortOrder('desc');
        documentMetrics.sortChanged({
          sort_field: 'created_at',
          sort_order: 'desc',
          page_type: 'documents',
        });
      }
    },
    [setSortBy, setSortOrder, documentMetrics],
  );

  // Define columns
  const columns: GridColDef<DocumentSummaryResponse>[] = useMemo(
    () => [
      {
        field: 'document_title',
        headerName: 'Title',
        flex: 2,
        minWidth: 200,
        // Prefer the Fast Card-extracted clinical `title`, then the
        // user-set `document_title` (legacy), then the filename. Sort
        // still goes through the API on the legacy `filename` column.
        valueGetter: (_value, row) => row.title || row.document_title || row.filename,
        renderCell: params => {
          const row = params.row;
          const kind = row.doc_kind ?? row.document_type ?? null;
          const Icon = kindIcon(kind);
          const color = kindStyle(kind);
          return (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', overflow: 'hidden' }}>
              <Tooltip title={kindLabel(kind)}>
                <Box
                  sx={{
                    color: color.fg,
                    bgcolor: color.bg,
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 16 }} />
                </Box>
              </Tooltip>
              <Stack spacing={0} sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap title={params.value as string}>
                  {params.value as string}
                </Typography>
                {row.author_provider_name && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {row.author_provider_name}
                    {row.author_facility ? ` · ${row.author_facility}` : ''}
                  </Typography>
                )}
              </Stack>
            </Stack>
          );
        },
      },
      {
        field: 'patient',
        headerName: subjectLanguageCap,
        flex: 1.5,
        minWidth: 150,
        sortable: false,
        renderCell: params => {
          const patient = params.row.patient;
          const patientId = params.row.patient_id;

          if (!patient && !patientId) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }

          const firstName = patient?.first_name || '';
          const lastName = patient?.last_name || '';
          const displayName =
            patient?.name ||
            patient?.preferred_name ||
            (firstName && lastName
              ? `${firstName} ${lastName}`
              : firstName || lastName || 'Unknown');

          return (
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', overflow: 'hidden' }}>
              {patientId && (
                <Avatar
                  id={patientId}
                  firstName={firstName || undefined}
                  lastName={lastName || undefined}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.75rem',
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography variant="body2" noWrap title={displayName}>
                {displayName}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'created_at',
        headerName: 'Date',
        width: 130,
        // Sort on the canonical effective_date (server-side it's
        // COALESCE(authored_at, created_at)); fall back to created_at for
        // legacy rows. Display resolves through the row itself so authored
        // docs render as a pure calendar date without a tz day-shift.
        valueGetter: (_value, row) => row.effective_date || row.created_at,
        valueFormatter: (_value, row) => formatDocumentDisplayDateMedium(row) ?? '',
      },
      {
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={e => handleDownloadClick(e, params.row)}
                aria-label="Download document"
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={e => handleViewClick(e, params.row)}
                aria-label="View document details"
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleViewClick, handleDownloadClick, subjectLanguageCap],
  );

  // Sort model for DataGrid
  const sortModel: GridSortModel = useMemo(
    () => [
      {
        field: sortBy === 'filename' ? 'document_title' : sortBy,
        sort: sortOrder,
      },
    ],
    [sortBy, sortOrder],
  );

  // Pagination model (DataGrid uses 0-based page index)
  const paginationModel: GridPaginationModel = useMemo(
    () => ({
      page: page - 1,
      pageSize,
    }),
    [page, pageSize],
  );

  const canGenerate = documentTypeInput.trim().length > 0 && !!selectedPatient;

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, md: 3 },
        gap: 3,
      }}
    >
      {/* Page Header */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <DocumentIcon color="action" sx={{ fontSize: 32 }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                Other Docs
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              View and generate documents
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            {hasCustomTemplates && (
              <Button
                variant="outlined"
                startIcon={<LibraryIcon />}
                onClick={() => navigate('/document-templates')}
                sx={{ textTransform: 'none' }}
              >
                Document Templates
              </Button>
            )}
            <UploadFilesButton
              onFilesSelected={files => void handlePageDrop(files)}
              size="medium"
              helperText="drag and drop"
            />
          </Stack>
        </Stack>
      </Box>

      {/* Entitlement Usage */}
      <EntitlementUsage capabilityName="document_creation" source="documents_page" />

      {/* Error Alert */}
      {error && (
        <Box sx={{ flexShrink: 0 }}>
          <Chip label={error} color="error" variant="outlined" />
        </Box>
      )}

      {/* Filters */}
      <Box sx={{ flexShrink: 0 }}>
        <DocumentFilters
          filters={filters}
          onPatientIdChange={setPatientId}
          onDocumentTypeChange={setDocumentType}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          isLoading={isLoading || isFetching}
          hideDocumentTypeFilter={true}
        />
      </Box>

      <Grid container spacing={3} sx={{ flex: 1, minHeight: 0, maxHeight: '100%' }}>
        {/* Left Column - Document List */}
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            maxHeight: '100%',
            gap: 1.5,
          }}
        >
          {pendingUploads.length > 0 && (
            <Stack spacing={1} sx={{ flexShrink: 0 }}>
              {pendingUploads.map(p => (
                <PendingUploadCard
                  key={p.artifactId}
                  filename={p.filename}
                  status={p.status}
                  documentReferences={p.documentReferences}
                />
              ))}
            </Stack>
          )}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              maxHeight: '100%',
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              '& .MuiDataGrid-root': {
                border: 'none',
                height: '100%',
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              },
            }}
          >
            <DataGrid
              rows={documents}
              columns={columns}
              getRowId={row => row.document_id}
              loading={isLoading || isFetching}
              pagination
              paginationMode="server"
              rowCount={totalRows}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[10, 20, 50]}
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              slotProps={{
                pagination: {
                  showFirstButton: true,
                  showLastButton: true,
                },
              }}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-footerContainer': {
                  minHeight: '52px',
                },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:focus': {
                  outline: 'none',
                },
              }}
              slots={{
                noRowsOverlay: () => (
                  <DocumentsEmptyState
                    icon={DocumentIcon}
                    message={
                      hasActiveFilters ? 'No documents match your filters' : 'No documents found'
                    }
                    primaryAction={
                      hasActiveFilters && (
                        <Typography
                          variant="body2"
                          color="primary"
                          sx={{ cursor: 'pointer' }}
                          onClick={clearFilters}
                        >
                          Clear filters
                        </Typography>
                      )
                    }
                    secondaryAction={
                      !hasActiveFilters && (
                        <GuideLink
                          surfaceId={GUIDE_SURFACES.DOCUMENTS_EMPTY_STATE}
                          shape="secondary-action"
                          slug="chart/patient-documents"
                        />
                      )
                    }
                  />
                ),
              }}
            />
          </Box>
        </Grid>

        {/* Right Column - Document Generation Panel */}
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '100%' }}
        >
          {!isMobile && (
            <EntitlementGate
              capabilityName="document_creation"
              source="documents_page_panel"
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: '100%',
              }}
              overlaySx={{ borderRadius: 2 }}
            >
              <Paper
                sx={{
                  flex: 1,
                  minHeight: 0,
                  maxHeight: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                {/* Header with Patient Picker */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Generate New Document
                  </Typography>
                  <PatientPicker
                    onPatientSelect={handlePatientSelect}
                    initialPatient={selectedPatient || undefined}
                    size="small"
                  />
                </Box>

                {/* Content Area */}
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 2,
                  }}
                >
                  {!selectedPatient ? (
                    // No patient selected - show prompt
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <DocumentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Select a {subjectLanguageCap}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ px: 4 }}>
                        Choose a {subjectLanguageCap.toLowerCase()} above to generate documents
                      </Typography>
                    </Box>
                  ) : (
                    // Patient selected - show document generation form
                    <Stack spacing={3}>
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <DocumentIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Describe the document you need and AI will help you create it.
                        </Typography>
                      </Box>

                      {/* Show custom template selector if user has saved templates */}
                      {hasCustomTemplates && (
                        <CustomTemplateSelector
                          onSelectTemplate={handleCustomTemplateSelect}
                          disabled={false}
                        />
                      )}

                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                          {hasCustomTemplates
                            ? 'Or describe a new document:'
                            : 'What document would you like to generate?'}
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          variant="outlined"
                          placeholder="e.g., Prior authorization for MRI, Referral letter to cardiologist..."
                          value={documentTypeInput}
                          onChange={e => setDocumentTypeInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey && canGenerate) {
                              e.preventDefault();
                              handleGenerateDocument();
                            }
                          }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          onClick={() => handleGenerateDocument()}
                          disabled={!canGenerate}
                          startIcon={<SendIcon />}
                        >
                          Generate
                        </Button>
                      </Box>

                      {/* Only show suggested templates if user doesn't have custom templates */}
                      {!hasCustomTemplates && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                            Or try a suggested template:
                          </Typography>
                          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                            {DOCUMENT_TEMPLATES.map(template => (
                              <Chip
                                key={template.name}
                                label={template.name}
                                onClick={() => handleTemplateClick(template)}
                                clickable
                                variant="outlined"
                                size="small"
                                sx={{ mb: 1 }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  )}
                </Box>
              </Paper>
            </EntitlementGate>
          )}
        </Grid>
      </Grid>

      {/* Document Detail Modal */}
      <DocumentDetailModal
        open={isDetailModalOpen}
        documentId={selectedDocumentId}
        onClose={handleCloseModal}
      />

      {isDragging && (
        <ArtifactDropOverlay
          patientName={
            selectedPatient
              ? [selectedPatient.first_name, selectedPatient.last_name].filter(Boolean).join(' ') ||
                'this patient'
              : 'your organization'
          }
          subtitle={uploadConstraintsSummary}
        />
      )}
    </Box>
  );
};

export default DocumentsListPage;
